import type HlsType from 'hls.js'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { Playback } from '../../../api/drama'
import { t } from '../../../i18n'
import {
  AUTO,
  cappedLevel,
  hasChosen,
  loadPreference,
  pickLevel,
  playableLevels,
  qualityLabel,
  savePreference,
  type LevelInfo,
} from '../quality'
import { PlayerControls } from './PlayerControls'
import { BOOST_RATE } from '../longPress'
import {
  DEFAULT_RATE,
  RATES,
  loadRate,
  loadRateLocked,
  rateLabel,
  saveRate,
  saveRateLocked,
} from '../playbackRate'
import { QualityToast, StallHint } from './QualityHints'
import { QuickMenu } from './QuickMenu'

interface Props {
  playback: Playback
  title: string
  episodeNumber: number
  posterUrl?: string
  /**
   * 由页面驱动埋点，播放器只负责把进度报出来。
   * `videoHeight` 是这一刻真正在播的清晰度档——取 video 元素的解码帧高度，
   * 不问 hls.js 要档位索引：iOS 走原生 HLS，那里没有 hls.js。
   */
  onProgress: (positionSeconds: number, durationSeconds: number, videoHeight: number) => void
  onStart: () => void
  onEnded: () => void
  /** 播放 / 暂停变了。选集面板里当前集的音柱跟着它动或停。 */
  onPlayingChange: (playing: boolean) => void
  /** 选集面板开着：视频缩在上方照常播，画面上的按钮和文字先藏起来（和 App 一样）。 */
  chromeHidden: boolean
  /** 取不到片子时通知页面。不报的话画面就是一直转圈，用户不知道发生了什么 */
  onFailed: () => void
  /**
   * 长按视频两侧顶倍速期间（从武装到松手）通知页面，页面据此临时关掉
   * EpisodeFeed 的竖滑换集判定——否则上滑锁定的位移会被当成切集的信号。
   */
  onBoostActiveChange: (active: boolean) => void
  /** 清屏播放。状态放在页面上，换集不丢。 */
  clean: boolean
  onCleanChange: (clean: boolean) => void
}

/**
 * 全屏竖屏播放器。直接用 hls.js，不做封装。
 *
 * 签名地址是有时限的，而且**每个切片都要过一次校验**：hls.js 边播边拉切片，
 * 签名一过期，剩下的切片全 403，表现是「看到一半突然卡死」。
 * 服务端据此按「一集时长的两倍 + 起播宽限」来签（playback.signLifetime），
 * 所以正常看完一集不会断；重新起播仍要重新申请 —— 页面每次都调 requestPlayback。
 *
 * 两处针对低端安卓的取舍：
 *   - 用 `hls.js/light`：我们的字幕是独立的 .vtt 走原生 <track>，
 *     不需要完整版里的 HLS 内嵌字幕、备用音轨、EME 那些能力。
 *   - 动态 import：首屏不该为播放器付费，等真要播了再拉这个 chunk。
 *
 * 这个组件只在「当前这一屏」渲染（见 EpisodeFeed）：相邻集只有封面，
 * 不预先建 video，更不预先建 hls 实例。
 */
/**
 * 把用户的选择落到 hls 上。
 *
 * 自动挡是「交回自适应 + 封顶 720」，固定档是钉死一档——**钉死之后 hls 不再
 * 按带宽降档**，这正是用户要的，但也因此卡顿时得由我们开口提醒他。
 */
function apply(hls: HlsType, usable: LevelInfo[], preferred: number): void {
  if (preferred === AUTO) {
    hls.currentLevel = -1
    hls.autoLevelCapping = cappedLevel(usable, hls.levels.length)
    return
  }
  hls.currentLevel = pickLevel(usable, preferred)
}

/** 多久之内卡这么多次，才算「网络确实不行」，而不是偶尔抖一下。 */
const STALL_WINDOW_MS = 15_000
const STALL_TIMES = 2

export function Player({
  playback,
  title,
  episodeNumber,
  posterUrl,
  onProgress,
  onStart,
  onEnded,
  onPlayingChange,
  chromeHidden,
  onFailed,
  onBoostActiveChange,
  clean,
  onCleanChange,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  // 带声音的自动播放被拦下来时，退而求其次静音播，并让用户一点就开声。
  // 什么都不做的话，进来看到的是一帧静止画面——那才是「坏了」的样子。
  const [mutedFallback, setMutedFallback] = useState(false)

  const hlsRef = useRef<HlsType | null>(null)
  /** 这一集能看的档。iOS 走原生 HLS，拿不到也换不了，那里就是空的。 */
  const [levels, setLevels] = useState<LevelInfo[]>([])
  /** 用户定的档（短边）。AUTO = 交给自适应。跨集跨会话记着。 */
  const [preferred, setPreferred] = useState(loadPreference)
  /** 视频设置面板开着没有 */
  const [menuOpen, setMenuOpen] = useState(false)
  const [rate, setRate] = useState(loadRate)
  const [rateLocked, setRateLocked] = useState(loadRateLocked)
  const [stallHint, setStallHint] = useState(false)
  /** 刚切完档的那条提示，几秒后自己消失。 */
  const [switched, setSwitched] = useState<string | null>(null)
  /** 这一集里被划掉过的建议不再出现。同一条提示反复弹比卡顿本身更烦。 */
  const dismissed = useRef(false)
  const stalls = useRef<number[]>([])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let destroy: (() => void) | null = null
    let cancelled = false

    // 先带声音试，被拦就静音再试一次。
    // 移动端浏览器只在「用户交互过」之后才允许带声音自动播，而从 Telegram
    // 的按钮点进来不算页面内的交互，所以第一集几乎一定会走到静音这一支；
    // 第二集起用户已经点过屏幕，就不会了。
    const start = () => {
      video.play().catch(() => {
        if (cancelled) return
        video.muted = true
        setMutedFallback(true)
        video.play().catch(() => {})
      })
    }

    void (async () => {
      const { default: Hls } = await import('hls.js/light')
      if (cancelled || !videoRef.current) return

      if (!Hls.isSupported()) {
        // iOS Safari 原生支持 HLS，不需要 hls.js
        video.src = playback.hlsUrl
        start()
        return
      }
      const hls = new Hls({
        // 低端安卓内存吃紧，缓冲放保守些
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        // 柬埔寨移动网络波动大，起播优先选低码率
        startLevel: 0,
      })
      // H5 封顶在短边 720：自适应只看带宽、不看解码能力，
      // 好 wifi 上的低端安卓会被推到 1080 然后掉帧，而画面上只表现为卡顿。
      // 封顶而不是在服务端另出一份删减的播放列表——那会让没有 1080 的集
      // 多出一个内容相同却要永远同步的重复文件。更高的档留给 App，它判断得了机型。
      //
      // iOS 走原生 HLS（上面那条 isSupported 分支直接 return 了），封顶管不到它；
      // 但 iPhone 解 1080 从来不是问题，要管的本就只有安卓这一侧。
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const usable = playableLevels(hls.levels)
        setLevels(usable)
        apply(hls, usable, loadPreference())
        start()
      })
      // 致命错误要冒泡到页面：切片 404、签名过期、网络断，
      // 在 hls.js 里都只是内部事件，画面上只表现为一直转圈。
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) onFailed()
      })
      hls.loadSource(playback.hlsUrl)
      hls.attachMedia(video)
      hlsRef.current = hls
      destroy = () => {
        hlsRef.current = null
        hls.destroy()
      }
    })()

    return () => {
      cancelled = true
      destroy?.()
      video.removeAttribute('src')
      video.load()
    }
    // onFailed 由页面用 useCallback 固定，不进依赖以免重建播放器
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playback.hlsUrl])

  const choose = useCallback((shortSide: number) => {
    setPreferred(shortSide)
    savePreference(shortSide)
    setMenuOpen(false)
    setStallHint(false)
    setSwitched(shortSide === AUTO ? t.quality.auto : qualityLabel(shortSide))
    const hls = hlsRef.current
    if (hls) apply(hls, playableLevels(hls.levels), shortSide)
  }, [])

  // 提示到点自己走。放在 effect 里而不是 setTimeout 里直接写，
  // 是为了组件被卸载（切集）时能跟着取消。
  useEffect(() => {
    if (!switched) return
    const timer = window.setTimeout(() => setSwitched(null), 2200)
    return () => window.clearTimeout(timer)
  }, [switched])

  /**
   * 把倍速落到 video 上。
   *
   * **两个属性都要设。** `playbackRate` 会被 media load 算法重置回
   * `defaultPlaybackRate`，而 `hls.attachMedia()` 内部正好会走那一段——
   * 只设 playbackRate 的话，切到下一集就悄悄变回 1 倍，而按钮上还写着 2.0x。
   */
  const applyRate = useCallback((video: HTMLVideoElement | null, next: number) => {
    if (!video) return
    video.defaultPlaybackRate = next
    video.playbackRate = next
  }, [])

  /** 改倍速并存下来。`locked` 只有长按上滑锁定时为真——手动选的倍速一律视为解锁。 */
  const setRateAndLock = useCallback(
    (next: number, locked: boolean) => {
      setRate(next)
      saveRate(next)
      setRateLocked(locked)
      saveRateLocked(locked)
      applyRate(videoRef.current, next)
    },
    [applyRate],
  )

  const chooseRate = useCallback(
    (next: number) => {
      setRateAndLock(next, false)
      setMenuOpen(false)
    },
    [setRateAndLock],
  )

  // 换集就是换一个 video 元素，属性不会跟过来，每一集都要重设一次
  useEffect(() => {
    applyRate(videoRef.current, rate)
  }, [applyRate, rate, playback.hlsUrl])

  /**
   * 卡住了。
   *
   * 只在**用户自己定过档**时开口：自动挡的人本来就交给自适应了，它会自己降，
   * 这时再弹一条「建议降档」纯属添乱。已经在最低档的也不提——没得降。
   */
  const onWaiting = useCallback(() => {
    if (dismissed.current || !hasChosen() || preferred === AUTO) return
    const now = Date.now()
    stalls.current = [...stalls.current.filter((at) => now - at < STALL_WINDOW_MS), now]
    const lower = levels.filter((l) => l.shortSide < preferred)
    if (stalls.current.length >= STALL_TIMES && lower.length > 0) setStallHint(true)
  }, [preferred, levels])

  /** 接受建议：降一档，不是直接摔到最低——那会让画面一下子糊得没必要。 */
  const downgrade = useCallback(() => {
    const lower = levels.filter((l) => l.shortSide < preferred)
    const next = lower[lower.length - 1]
    if (next) choose(next.shortSide)
    else setStallHint(false)
  }, [levels, preferred, choose])

  /** 点画面：静音兜底时先开声音（这一下不该顺手把片子停掉），其余时候是播放/暂停。 */
  const tap = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (mutedFallback) {
      video.muted = false
      setMutedFallback(false)
      if (video.paused) void video.play()
      return
    }
    if (video.paused) void video.play()
    else video.pause()
  }, [mutedFallback])

  return (
    <div style={styles.wrap}>
      <video
        ref={videoRef}
        className="player-video"
        style={styles.video}
        playsInline
        autoPlay
        // 封面兜住起播前的那一两百毫秒。没有它，切过来的第一眼是纯黑。
        poster={posterUrl}
        preload="metadata"
        onPlay={onStart}
        onEnded={onEnded}
        onWaiting={onWaiting}
        // attachMedia 和上面那个 effect 谁先跑不由我们定，所以这里再钉一次
        onLoadedMetadata={(e) => applyRate(e.currentTarget, rate)}
        onError={onFailed}
        onTimeUpdate={(e) => {
          const v = e.currentTarget
          onProgress(v.currentTime, v.duration || 0, v.videoHeight)
        }}
      >
        {playback.subtitleUrl && (
          <track kind="subtitles" srcLang="km" label="ភាសាខ្មែរ" src={playback.subtitleUrl} default />
        )}
      </video>

      {/*
        溯源水印。挡不住录屏，但流出的副本能定位到人，
        且带着别人 ID 的盗版传播价值大减。位置随机漂移，增加裁剪成本。
      */}
      {playback.watermark && <Watermark text={playback.watermark} />}

      <PlayerControls
        videoRef={videoRef}
        title={title}
        episodeNumber={episodeNumber}
        muted={mutedFallback}
        rate={rate}
        rateLabel={rateLabel(rate)}
        rateActive={rate !== DEFAULT_RATE}
        rateLocked={rateLocked}
        onOpenRate={() => setMenuOpen(true)}
        onOpenQuickMenu={() => setMenuOpen(true)}
        onLockRate={() => setRateAndLock(BOOST_RATE, true)}
        onUnlockRate={() => setRateAndLock(DEFAULT_RATE, false)}
        onBoostActiveChange={onBoostActiveChange}
        clean={clean}
        onExitClean={() => onCleanChange(false)}
        hint={
          switched ? (
            <QualityToast label={switched} />
          ) : stallHint ? (
            <StallHint
              onAccept={downgrade}
              onDismiss={() => {
                dismissed.current = true
                setStallHint(false)
              }}
            />
          ) : null
        }
        onTap={tap}
        onPlayingChange={onPlayingChange}
        hidden={chromeHidden}
      />

      {menuOpen && (
        <QuickMenu
          rate={rate}
          rates={RATES.map((r) => ({ value: r, label: rateLabel(r) }))}
          onPickRate={chooseRate}
          // 高的排前面，和所有播放器一致；「自动」摆最前。iOS 走原生 HLS，档位拿不到也换不了，那里不摆这一行
          qualities={
            levels.length > 1
              ? [
                  { value: AUTO, label: t.quality.auto },
                  ...[...levels].reverse().map((l) => ({ value: l.shortSide, label: qualityLabel(l.shortSide) })),
                ]
              : undefined
          }
          quality={preferred}
          onPickQuality={choose}
          clean={clean}
          onToggleClean={() => {
            onCleanChange(!clean)
            setMenuOpen(false)
          }}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  )
}

/** 水印每 15 秒换一个位置。九宫格取点，避开正中央以免挡脸。 */
function Watermark({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const spots = [
      { top: '8%', left: '6%' },
      { top: '8%', right: '6%' },
      { top: '42%', left: '6%' },
      { top: '42%', right: '6%' },
      { bottom: '18%', left: '6%' },
      { bottom: '18%', right: '6%' },
    ]
    let i = 0
    const move = () => {
      const el = ref.current
      if (!el) return
      el.style.cssText = ''
      Object.assign(el.style, styles.watermark as Record<string, string>)
      const spot = spots[i % spots.length]
      if (spot) Object.assign(el.style, spot)
      i += 1
    }
    move()
    const timer = window.setInterval(move, 15_000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div ref={ref} style={styles.watermark}>
      {text}
    </div>
  )
}

const styles = {
  wrap: {
    position: 'absolute',
    inset: 0,
    background: '#000',
    overflow: 'hidden',
  },
  // object-fit 在 global.css 的 .player-video 里按视口比例分情况给，内联表达不了媒体查询
  video: { width: '100%', height: '100%', display: 'block' },
  watermark: {
    position: 'absolute',
    top: '8%',
    left: '6%',
    fontSize: '12px',
    color: 'rgba(255,255,255,.34)',
    letterSpacing: '.08em',
    pointerEvents: 'none',
    userSelect: 'none',
    textShadow: '0 1px 2px rgba(0,0,0,.6)',
  },
} satisfies Record<string, React.CSSProperties>
